<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
import { hall } from '@/hall'
import { GameType } from '@/defines/EnumDefine'
import defaultGameIcon from '@/assets/hall/game_icon_default.webp'
import { ServerDefine } from '@/config/ServerDefine'

const props = defineProps({
  gameType: {
    type: Number,
    required: true,
  },
})

interface GameDisplay {
  id: number
  name: string
  icon: string
  isTop: boolean
  isBuy: boolean
}

const allGames = ref<GameDisplay[]>([])
const displayedResults = ref<GameDisplay[]>([])
const displayCount = ref(12)

const refreshGames = () => {
  if (props.gameType === GameType.All) return

  const gameIds = hall.hallModule.getSubgameIDList(props.gameType, 0) || []

  allGames.value = gameIds.map((id) => {
    let subInfo = hall.hallModule.getSubgameInfo(id)
    if (!subInfo) {
      subInfo = { gid: id, Name: hall.hallModule.getSubgameName(id) }
    }
    const gid = subInfo.gid || id
    return {
      id: gid,
      name: subInfo.Name || subInfo.name || 'Unknown',
      icon: ServerDefine.GAME_ICON_URL + gid + '.png',
      isTop: hall.hallModule.isHotGame(id),
      isBuy: false,
    }
  })

  displayedResults.value = allGames.value
  displayCount.value = 12
}

const visibleGames = computed(() => {
  return displayedResults.value.slice(0, displayCount.value)
})

const handleLoadMore = () => {
  displayCount.value += 12
}

const openGame = (gameId: number) => {
  hall.hallModule.postSubgameFree(gameId)
}

const handleImageError = (e: Event) => {
  const target = e.target as HTMLImageElement
  target.onerror = null
  target.src = defaultGameIcon
}

watch(
  () => props.gameType,
  () => {
    refreshGames()
  },
)

onMounted(() => {
  refreshGames()
  hall.eventDispatcher.on(hall.EventDefine.RefreshSubgameList, refreshGames)
})

onUnmounted(() => {
  hall.eventDispatcher.off(hall.EventDefine.RefreshSubgameList, refreshGames)
})
</script>

<template>
  <div class="single-category-games">
    <div class="results-grid" v-if="visibleGames.length > 0">
      <div
        v-for="game in visibleGames"
        :key="game.id"
        class="game-card item-small cursor-pointer"
        @click="openGame(game.id)"
      >
        <img
          :src="game.icon || defaultGameIcon"
          class="game-img"
          :alt="game.name"
          @error="handleImageError"
        />
        <span v-if="game.isTop" class="tag top">TOP</span>
      </div>
    </div>

    <div v-else class="no-games-placeholder">No games currently available in this category.</div>

    <!-- Pagination Info and Load More -->
    <div class="tray-footer" v-if="displayedResults.length > 0">
      <div class="tray-count">
        Current
        <span class="red-text">{{ visibleGames.length }}/{{ displayedResults.length }}</span>
        Games
      </div>
      <button
        v-if="displayedResults.length > visibleGames.length"
        @click="handleLoadMore"
        class="btn-load-more"
      >
        Load More
      </button>
    </div>
  </div>
</template>

<style scoped>
.single-category-games {
  padding: 10px;
}

.results-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.game-card {
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  background: transparent;
}

.game-img {
  width: 100%;
  height: auto;
  display: block;
}

.tag {
  position: absolute;
  font-size: 0.6rem;
  font-weight: 800;
  padding: 2px 6px;
  border-radius: 4px;
  z-index: 2;
}

.tag.top {
  top: 5px;
  left: 5px;
  background: #ff4500;
  color: white;
}

.tray-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 30px;
  margin-bottom: 30px;
  gap: 15px;
}

.tray-count {
  font-size: 1rem;
  color: #888;
}

.red-text {
  color: #ff4d4d;
  font-weight: bold;
}

.btn-load-more {
  background: #4caf50;
  color: white;
  border: none;
  padding: 12px 40px;
  border-radius: 6px;
  font-weight: bold;
  font-size: 1.1rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  cursor: pointer;
}

.btn-load-more:active {
  transform: scale(0.95);
}

.no-games-placeholder {
  text-align: center;
  color: #888;
  margin-top: 50px;
  font-size: 1.1rem;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
